<template>
<div>
	<h2>Equipment Location</h2>

	<div v-if="$store.equipmentGeoData">

		<field label="Show Dispatched Service Jobs"
			col-class="col-auto"
			:params="{type:'boolean'}"
			v-model="showJobs"
			v-on:change="showJobsChange">
		</field>

		Last Updated: {{ formatDateTimeToLocal($store.equipmentGeoData.dateTime) }}

		<table class="table table-sm table-bordered">
			<tbody>
				<tr>
					<th>Latitude</th>
					<td>{{ $store.equipmentGeoData.latitude }}</td>
					<th>Speed</th>
					<td>{{ $store.equipmentGeoData.speed }} km/h</td>
				</tr>
				<tr>
					<th>Longitude</th>
					<td>{{ $store.equipmentGeoData.longitude }}</td>
					<th>Currently Driving</th>
					<td>{{ $store.equipmentGeoData.isDriving ? 'Yes' : 'No' }}</td>
				</tr>
				<tr v-if="scheduledService">
					<th>Currently Scheduled</th>
					<td>Service #{{ scheduledService.job_num }}: {{ scheduledService.customer_name_cache ? scheduledService.customer_name_cache : scheduledService.job_name }}</td>
					<td colspan="2">
						<div><strong>Distance From: </strong> {{ distanceFromScheduledJob }} km</div>
						<div><strong>ETA: </strong> {{ estimatedDrivingTimeMinutes }} min</div>
					</td>
				</tr>
				<tr v-else-if="scheduledProject">
					<th>Currently Scheduled</th>
					<td>Project #{{ scheduledProject.job_num }}: {{ scheduledProject.customer_name_cache ? scheduledProject.customer_name_cache : scheduledProject.job_name }}</td>
					<td colspan="2">
						<div><strong>Distance From: </strong> {{ distanceFromScheduledJob }} km</div>
						<div><strong>ETA: </strong> {{ estimatedDrivingTimeMinutes }} min</div>
					</td>
				</tr>
				<tr v-if="clockedIntoJob">
					<th>Clocked Into</th>
					<td><router-link :to="clockedIntoJobLink">{{ clockedIntoJobType }} #{{ clockedIntoJob.job_num }}: {{ clockedIntoJob.customer_name_cache ? clockedIntoJob.customer_name_cache : clockedIntoJob.job_name }}</router-link></td>
					<td colspan="2">
						<div><strong>Distance From: </strong> {{ distanceFromClockedIntoJob }} km</div>
						<div v-if="estimatedDrivingTimeMinutes"><strong>ETA: </strong> {{ estimatedDrivingTimeMinutes }} min</div>
					</td>
				</tr>
			</tbody>
		</table>

		<div class="d-flex align-items-center">
			<strong class="mr-2">Start Date/Time</strong>
			<field 
				:params="{type: 'date'}"
				v-model="timeRange.startDate">
			</field>

			<field class="mr-2"
				:params="{type: 'select',options:[
					{text: '12 AM',value:0},
					{text: '1:00 AM',value:'01'},
					{text: '2:00 AM',value:'02'},
					{text: '3:00 AM',value:'03'},
					{text: '4:00 AM',value:'04'},
					{text: '5:00 AM',value:'05'},
					{text: '6:00 AM',value:'06'},
					{text: '7:00 AM',value:'07'},
					{text: '8:00 AM',value:'08'},
					{text: '9:00 AM',value:'09'},
					{text: '10:00 AM',value:10},
					{text: '11:00 AM',value:11},
					{text: '12:00 PM',value:12},
					{text: '1:00 PM',value:13},
					{text: '2:00 PM',value:14},
					{text: '3:00 PM',value:15},
					{text: '4:00 PM',value:16},
					{text: '5:00 PM',value:17},
					{text: '6:00 PM',value:18},
					{text: '7:00 PM',value:19},
					{text: '8:00 PM',value:20},
					{text: '9:00 PM',value:21},
					{text: '10:00 PM',value:22},
					{text: '11:00 PM',value:23},

				]}" 
				v-model="timeRange.startTime">
			</field>

			<strong class="mr-2">Duration</strong>
			<field class="mr-2"
				:params="{type: 'select',options:[
					{text: '1 hr',value:1},
					{text: '2 hr',value:2},
					{text: '3 hr',value:3},
					{text: '4 hr',value:4},
					{text: '5 hr',value:5},
					{text: '6 hr',value:6},
					{text: '7 hr',value:7},
					{text: '8 hr',value:8},
					{text: '9 hr',value:9},
					{text: '10 hr',value:10},
					{text: '11 hr',value:11},
					{text: '12 hr',value:12}
				]}"
				v-model="timeRange.duration">
			</field>

			<button type="button" class="btn btn-primary" v-on:click="updateTrail">Update</button>
		</div>

		<h4>Timeline</h4>
		<equipment-timeline :timeline-events="combinedTimelineEvents"></equipment-timeline>

		<h4>Trail</h4>
		<vue-map
			:center="{
				lat: +$store.equipmentGeoData.latitude,
				lng: +$store.equipmentGeoData.longitude
			}"
			:zoom="12"
			map-size="map-lg"
			:markers="[
				geoDataMarker,
				...jobMarkers
			]"
			:circles="jobCircles"
			:lines="mutatedTrail">
		</vue-map>
	</div>
	<alert v-else-if="geoDataError" :message="geoDataError" unclosable="true" classes="alert-danger" visible="true">
	</alert>

	<location-selector 
		v-else-if="equipmentItem && Object.keys(equipmentItem).length && $store.company"
		:initial-values="initialLocationValues"
		v-on:change="setEquipmentLocation">
	</location-selector>
</div>
</template>
<script type="text/javascript">
import LocationSelector from "@/components/super_components/LocationSelector"
import VueMap from "@/components/VueMap"
import Field from "@/components/Field"
import Alert from "@/components/Alert"
import FormGroup from "@/components/FormGroup"
import EquipmentTimeline from "@/components/EquipmentTimeline"

import FleetTrackingMixins from "@/mixins/FleetTrackingMixins.js"

export default {
	name: "EquipmentItemLocation",
	components: {
		VueMap,
		LocationSelector,
		Field,
		Alert,
		FormGroup,
		EquipmentTimeline
	},
	mixins: [FleetTrackingMixins],
	props: ["equipmentItem"],
	data() {
		return {
			geoDataError: "",
			pollTimer: null,
			showJobs: false,
			dispatchedServices: [],
			scheduledOnServiceId: null,
			scheduledOnProjectId: null,
			scheduledService: null,
			scheduledProject: null,
			estimatedDrivingTimeMinutes: false,
			clockedIntoJob: null,
			clockedIntoJobType: null,
			trail: [],
			activityTimelineList: [],
			equipmentForms: [],
			equipmentScheduleEvents: [],
			timeRange: {
				startDate: null,
				startTime: 8,
				duration: 1
			}
		}
	},
	watch: {
		equipmentItem() {
			if (this.isEquipmentItemSet)
				this.fetchData()
		},
		scheduledService() {
			if (this.equipmentGeoData)
				this.fetchDrivingTime()
		},
		equipmentGeoData() {
			if (this.scheduledService)
				this.fetchDrivingTime()
		}
	},
	computed: {
		initialLocationValues() {
			let values = {
				location_type: 'address'
			}

			const addressFields = ['suite', 'address', 'city', 'province', 'country', 'postal_code']
			const pinFields = ['lat', 'long']
			const allFields = ['location_type', ...addressFields, ...pinFields]

			if (this.equipmentItem) {

				// Pre-populate with equipment fields
				allFields.forEach(key => {
					if (key in this.equipmentItem && this.equipmentItem[key] &&    // Key is valid
						(!pinFields.includes(key) || +this.equipmentItem[key]))     // If the key is long/lat, it is also not zero)
					{
						values[key] = this.equipmentItem[key]
					}
				})
			}

			// Fill in any that aren't filled in already
			allFields.forEach(key => {
				if (!(key in values) && key in this.$store.company && this.$store.company[key] &&  // Key is valid
					(!pinFields.includes(key) || +this.$store.company[key]))                        // If the key is long/lat, it is also not zero)
				{
					values[key] = this.$store.company[key]
				}
			})

			// normalize country name, if required
			if (!('country' in values) || values.country.toLowerCase() == 'canada')
				values.country = "CA"

			return values
		},
		combinedTimelineEvents() {
			let vm = this

			let combined = [
				...vm.activityTimelineList,
				...vm.getArrivalEvents()
			]

			this.equipmentForms.forEach(form => {
				let item = {
					dateTime: vm.moment(form.created_at).toISOString(),
					description: "Form created: " + form.FormChildTemplate.description,
					type: "CompanyFormCreation"
				}

				if ("CreatedBy" in form && form.CreatedBy)
					item.description += " by " + form.CreatedBy.first_name + " " + form.CreatedBy.last_name,

				combined.push(item)
			})

			return combined
		},
		geoDataMarker() {
			return {
				lat: +this.$store.equipmentGeoData.latitude,
				lng: +this.$store.equipmentGeoData.longitude,
				icon: {
					anchor: {
						x: 18,
						y: 18
					},
					url:  window.apiRoot+"resources/icons/truck_36.png",
				}
			}
		},
		jobMarkers() {
			if (!this.showJobs)
				return []

			let markers = this.dispatchedServices.map(service => {
				return {
					lat: +service.lat,
					lng: +service.long,
					icon: window.apiRoot+"resources/icons/hardhat_36_point.png",
					infoWindowText: "#" + service.job_num + ": " + service.customer_name_cache
				}
			})

			let targetJob = this.getTargetJob()
			
			if (targetJob) {
				//draw scheduled service marker
				markers.push({
					lat: +targetJob.lat,
					lng: +targetJob.long,
					icon: window.apiRoot+"resources/icons/hardhat_36_point.png",
					infoWindowText: "#" + targetJob.job_num + ": " + targetJob.customer_name_cache
				})
			}

			return markers
		},
		jobCircles() {
			let vm = this

			let circles = []

			if (this.showJobs) {
				circles = this.dispatchedServices.map(service => {

					let strokeColor = '#ffc107'
					let fillColor = '#ffc1074f'

					return {
						lat: +service.lat,
						lng: +service.long,
						radius: +service.check_in_tolerance,
						strokeColor,
						fillColor
					}
				})
			}

			//draw an extra cicle to draw attention to the scheduled service
			let targetJob = this.getTargetJob()

			if (targetJob) {
				circles.push({
					lat: +targetJob.lat,
					lng: +targetJob.long,
					radius: +targetJob.check_in_tolerance,
					strokeColor: "purple",
					fillColor: "purple"
				})
			}

			return circles
		},
		distanceFromTargetJob() {
			let job = this.getTargetJob()

			if (job) {
				let distanceKm = this.getDistance(job.lat,job.long,this.$store.equipmentGeoData.latitude, this.$store.equipmentGeoData.longitude)/1000

				return distanceKm.round(1)
			}
			else return false
		},
		distanceFromScheduledJob() {
			let scheduledJob = this.scheduledService ? this.scheduledService : this.scheduledProject

			if (!scheduledJob)
				return false

			let distanceKm = this.getDistance(scheduledJob.lat,scheduledJob.long,this.$store.equipmentGeoData.latitude, this.$store.equipmentGeoData.longitude)/1000

			return distanceKm.round(1)
		},
		distanceFromClockedIntoJob() {
			if (!this.clockedIntoJob)
				return false

			let distanceKm = this.getDistance(this.clockedIntoJob.lat,this.clockedIntoJob.long,this.$store.equipmentGeoData.latitude, this.$store.equipmentGeoData.longitude)/1000

			return distanceKm.round(1)
		},
		clockedIntoJobLink() {
			let link = "/"
			if (this.clockedIntoJobType = "Project")
				link += "project/"
			else
				link += "service/"

			link += this.clockedIntoJob.slug + "/dashboard"

			return link
		},
		mutatedTrail() {
			let vm = this
			let mutated = this.trail.map(point => {
				point.lat = point.latitude
				point.lng = point.longitude

				return point
			})

			let trailSections = []
			let state = ""
			let lastMaxSpeed = -1

			this.trail.forEach((point,index) => {

				let lastPoint = false
				if (index > 0)
					lastPoint = vm.cloneObj(this.trail[index - 1])
				else
					lastPoint = vm.cloneObj(point)

				delete lastPoint.maxSpeed

				if ("maxSpeed" in point && point.maxSpeed != lastMaxSpeed) {
					let strokeColor = "darkgreen"

					if (point.speed > point.maxSpeed + 5)
						strokeColor = "red"
					else 
						strokeColor = "limegreen"

					trailSections.push({
						trail: [lastPoint,point],
						strokeColor
					})	
				}
				else if (!("maxSpeed" in point) || point.maxSpeed == -1) {
					//no speed limit data
					if (state != "unknown") {
						state = "unknown"
						//create new section
						trailSections.push({
							trail: [lastPoint,point],
							strokeColor: 'darkgreen'
						})
					}
					else 
						trailSections[trailSections.length - 1].trail.push(point)
					
				}
				else if ("maxSpeed" in point && point.maxSpeed > 0 && point.speed > point.maxSpeed + 5) {
					if (state != "speeding") {
						state = "speeding"

						//create new section
						trailSections.push({
							trail: [lastPoint,point],
							strokeColor: 'red'
						})
					}
					else 
						trailSections[trailSections.length - 1].trail.push(point)
					
				}
				else {
					if (state != "notspeeding") {
						state = "notspeeding"
						//create new section
						trailSections.push({
							trail: [lastPoint,point],
							strokeColor: 'limegreen'
						})
					}
					else
						trailSections[trailSections.length - 1].trail.push(point)
				}
			})

			return trailSections
		},
		isEquipmentItemSet() {
			return this.equipmentItem && Object.keys(this.equipmentItem).length > 0
		},
	},
	methods: {
		getTargetJob() {
			let targetJob = false
			if (this.clockedIntoJob) {
				targetJob = this.clockedIntoJob
			}
			else {
				if (this.scheduledService) {
					targetJob = this.scheduledService
					targetJob.type = "Service"
				}
				else if (this.scheduledProject) {
					targetJob = this.scheduledProject
					targetJob.type = "Project"
				}
			}
			return targetJob
		},
		getScheduledJob() {
			const now = moment(); // assumes moment.js is available

			const currentJobEvent = this.equipmentScheduleEvents.find(event => {
				const start = moment(event.start_time);
				const end = moment(event.end_time);

				return now.isBetween(start, end);
			});

			if (currentJobEvent && "Service" in currentJobEvent && currentJobEvent.Service) {
				this.scheduledService = currentJobEvent.Service
				return currentJobEvent.Service
			}
			if (currentJobEvent && "Job" in currentJobEvent && currentJobEvent.Job) {
				this.scheduledProject = currentJobEvent.Job
				return currentJobEvent.Job
			}

			return false;
		},
		setEquipmentLocation(location) {
			this.$emit("equipmentLocationChanged",location)
		},
		showJobsChange(value) {
			if (value) {
				this.fetchDispatchedServices()
			}
		},
		updateTrail() {
			this.fetchTrail(this.equipmentItem.geotab_entity_id)
			this.fetchEquipmentTrackerEventLogs()
		},
		getArrivalEvents() {
			const arrivalEvents = [], vm = this

			this.equipmentScheduleEvents.forEach(event => {
				let jobLat = parseFloat(event.Service && event.Service.lat);
				let jobLng = parseFloat(event.Service && event.Service.long);
				let radius = parseFloat((event.Service && event.Service.check_in_tolerance) || 100);
				if (!jobLat || !jobLng) return;

				// Sort GPS points in ascending time
				const sortedTrail = this.trail.slice().sort((a, b) =>
					vm.moment(a.dateTime).isAfter(vm.moment(b.dateTime)) ? 1 : -1
				);

				for (let i = 0; i < sortedTrail.length; i++) {
					const point = sortedTrail[i];
					const dist = vm.getDistance(jobLat, jobLng, point.latitude, point.longitude);

					if (dist <= radius) {

						let jobNum = false, jobType = "Service"
						if ("Service" in event && event.Service) {
							jobNum = event.Service.job_num
						}
						else if ("Job" in event && event.Job) {
							jobNum = event.Job.job_num
							jobType = "Project"
						}

						arrivalEvents.push({
							description: 'Arrived at ' + jobType + ' #' + jobNum,
							dateTime: point.dateTime,
							type: 'ArrivalEvent',
							serviceId: event.service_id,
							jobLat,
							jobLng,
							arrivalLat: point.latitude,
							arrivalLng: point.longitude,
							distance: dist.toFixed(1)
						});
						break; // Only log the first entry into the geofence
					}
				}
			});

			return arrivalEvents;
		},
		fetchEquipmentGeoData() {
			let vm = this

			if (this.equipmentItem && "id" in this.equipmentItem) {

				if (!this.$store.equipmentGeoData && !this.$store.fetchingGeoData) {
					this.$store.fetchingGeoData = true
					this.getJSON("Equipment/get-geo-data",{id: vm.equipmentItem.id}).then(response => {
						if (response.success) {
							vm.$store.equipmentGeoData = response.data

							if (vm.equipmentItem.assigned_user_id > 0) 
								vm.fetchClockedIntoJob()

							vm.$forceUpdate()
						}
						else if ("errors" in response) {

							vm.geoDataError = response.errors[0]
						}
					})
				}
				else {
					if (vm.equipmentItem.assigned_user_id > 0) 
						vm.fetchClockedIntoJob()

					vm.$forceUpdate()
				}
			}
		},
		fetchDispatchedServices() {
			let vm = this

			let params = {
				filters: [
					["status","Dispatched"]
				],
				fields: ["id","slug","lat","long","check_in_tolerance","appointment_time","due_date","status","assigned_to","job_num","job_name","customer_name_cache"]
			}

			this.getJSON("Service/get",params).then(response => {
				if (response.success)
					vm.dispatchedServices = response.data
			})
		},
		pollLocation() {
			let vm = this

			this.getJSON("Equipment/poll-location",{geotab_entity_id: vm.equipmentItem.geotab_entity_id}).then(response => {
				if (response.success) {
					let fields = [
						"latitude","longitude","speed","isDriving","bearing","currentStateDuration","dateTime"
					]

					fields.forEach(field => {
						vm.$store.equipmentGeoData[field] = response.data[field]
					})
				}
			})
		},
		fetchScheduledService() {
			let vm = this

			this.getJSON("Service/find/" + vm.scheduledOnServiceId).then(response => {
				if (response.success) {
					vm.scheduledService = response.data

					if (vm.scheduledService && vm.$store.equipmentGeoData)
						vm.fetchDrivingTime()
				}
			})
		},
		fetchScheduledProject() {
			let vm = this

			this.getJSON("Job/find/" + vm.scheduledOnProjectId).then(response => {
				if (response.success) {
					vm.scheduledProject = response.data

					if (vm.scheduledProject && vm.$store.equipmentGeoData)
						vm.fetchDrivingTime()
				}
			})
		},
		fetchTrail(deviceId) {
			let vm = this

			let fromDateTimeMoment = this.moment(this.timeRange.startDate + " " + this.timeRange.startTime + ":00")
			let fromDateTime = fromDateTimeMoment.utc().format("YYYY-MM-DD HH:mm")


			let toDateTimeMoment = fromDateTimeMoment.add(this.timeRange.duration,"hours")
			let toDateTime = toDateTimeMoment.utc().format("YYYY-MM-DD HH:mm")

			return new Promise((resolve,reject) => {
				vm.getJSON("TrackingDevice/get-trail",{
						id: deviceId,
						fromDateTime,
						toDateTime
					}).then(response => {

					if (response.success) {
						vm.trail = response.data
					}
				})
			})
		},
		fetchEquipmentTrackerEventLogs() {
			let vm = this

			let fromDateTimeMoment = this.moment(this.timeRange.startDate + " " + this.timeRange.startTime + ":00")
			let fromDateTime = fromDateTimeMoment.utc().format("YYYY-MM-DD HH:mm")

			let toDateTimeMoment = fromDateTimeMoment.add(this.timeRange.duration,"hours")
			let toDateTime = toDateTimeMoment.utc().format("YYYY-MM-DD HH:mm")

			let params = {
				id: vm.equipmentItem.geotab_entity_id,
				fromDateTime,
				toDateTime
			}

			this.getJSON("TrackingDevice/get-event-log",params).then(response => {
				if (response.success) {
					vm.activityTimelineList = response.data
				}
			})
		},
		fetchDrivingTime() {
			let vm = this

			let job = this.getTargetJob()

			if (job && vm.$store.equipmentGeoData) {
				this.getJSON("EquipmentGeoData/driving-distance",{
					origin_address: vm.$store.equipmentGeoData.latitude + ", " + vm.$store.equipmentGeoData.longitude,
					destination_address: job.lat + ", " + job.long,
					unit: "minutes"
				}).then(response => {
					if (response.success) {
						let min = +response.data.distance
						vm.estimatedDrivingTimeMinutes = min.round(0)
					}
				})
			}
		},
		fetchEquipmentScheduleEvents() {
			let vm = this

			let params = {
				filters: [
					["equipment_id",vm.equipmentItem.id],
					["start_time <= ",this.moment().utc().format("YYYY-MM-DD HH:mm")],
					["end_time >",this.moment().utc().format("YYYY-MM-DD HH:mm")]
				],
				include: ["Service","Job"]
			}

			this.getJSON("ScheduleEvent/get",params).then(response => {
				if (response.success) {

					vm.equipmentScheduleEvents = response.data

					if (response.data.length > 0) {
						if (response.data[0].service_id > 0) {
							vm.scheduledOnServiceId = response.data[0].service_id 
							vm.fetchScheduledService()
						}
						else if (response.data[0].job_id > 0) {
							vm.scheduledOnProjectId = response.data[0].job_id
							vm.fetchScheduledProject()
						}
					}
				}
			})
		},
		fetchClockedIntoJob() {
			let vm = this

			let params = {
				filters: [
					["user_id",vm.equipmentItem.assigned_user_id]
				],
				include: ["Job","Service"]
			}

			this.getJSON("OpenTimeLog/get",params).then(response => {
				if (response.success && response.data.length > 0) {
					if ("Job" in response.data[0] && response.data[0].Job) {
						vm.clockedIntoJobType = "Project"
						vm.clockedIntoJob = response.data[0].Job
					}
					if ("Service" in response.data[0] && response.data[0].Service) {
						vm.clockedIntoJobType = "Service"
						vm.clockedIntoJob = response.data[0].Service
					}

					vm.fetchDrivingTime()
				}
			})
		},
		fetchEquipmentFormSaves() {
			let vm = this

			let fromDateTimeMoment = this.moment(this.timeRange.startDate + " " + this.timeRange.startTime + ":00")
			let fromDateTime = fromDateTimeMoment.utc().format("YYYY-MM-DD HH:mm")


			let toDateTimeMoment = fromDateTimeMoment.add(this.timeRange.duration,"hours")
			let toDateTime = toDateTimeMoment.utc().format("YYYY-MM-DD HH:mm")

			let params = {
				filters: [
					["equipment_id",vm.equipmentItem.id],
					["created_at >= ", fromDateTime],
					["created_at <= ", toDateTime]
				],
				fields: ["id","slug","created_at","FormChildTemplate","CreatedBy","created_at","created_by"],
				include: [
					{
						model: "FormChildTemplate",
						fields: ["id","slug","description"]
					},
					{
						model: "User",
						fields: ["id","slug","first_name","last_name"]
					}
				]
			}

			return new Promise((resolve,reject) => {
				vm.getJSON("CompanyForm/get",params).then(response => {
					if (response.success) {
						vm.equipmentForms = response.data
					}
					resolve(response)
				})
			})
		},
		fetchData() {
			let vm = this

			this.dataFetched = true

			return new Promise((resolve,reject) => {

				let promises = [
					vm.fetchEquipmentGeoData(),
					vm.fetchEquipmentScheduleEvents(),
					vm.fetchEquipmentTrackerEventLogs(),
					vm.fetchEquipmentFormSaves(),
				]

				if (vm.isEquipmentItemSet && "geotab_entity_id" in vm.equipmentItem)
					promises.push(vm.fetchTrail(vm.equipmentItem.geotab_entity_id))

				Promise.all(promises).then(responses => {
					resolve(responses)
				})
			})
		}
	},
	created() {
		let vm = this

		this.timeRange.startDate = this.moment().format("YYYY-MM-DD")
		this.timeRange.startTime = this.moment().subtract(2,"hours").format("HH")
		this.timeRange.duration = 3

		if (this.isEquipmentItemSet) {
			this.fetchData()
		}

		this.pollTimer = setInterval(() => {
			vm.pollLocation()
		},60000)
	},
	destroyed() {
		clearInterval(this.pollTimer)
	}
}
</script>